import {
  Check,
  Ellipsis,
  Infinity as InfinityIcon,
  Mars,
  Venus,
  X,
  type LucideIcon,
} from "lucide-react"

import AirIcon from "@/assets/img/astro-map/icons/air.svg?react"
import AmbivertIcon from "@/assets/img/astro-map/icons/ambivert.svg?react"
import EarthIcon from "@/assets/img/astro-map/icons/earth.svg?react"
import ExtrovertIcon from "@/assets/img/astro-map/icons/extrovert.svg?react"
import FireIcon from "@/assets/img/astro-map/icons/fire.svg?react"
import IntrovertIcon from "@/assets/img/astro-map/icons/introvert.svg?react"
import WaterIcon from "@/assets/img/astro-map/icons/water.svg?react"
import ActivitiesIcon from "@/assets/img/soulmate/icons/activities.svg?react"
import GesturesIcon from "@/assets/img/soulmate/icons/gestures.svg?react"
import GiftIcon from "@/assets/img/soulmate/icons/gift.svg?react"
import HandIcon from "@/assets/img/soulmate/icons/hand.svg?react"
import HomeIcon from "@/assets/img/soulmate/icons/home.svg?react"
import MessageIcon from "@/assets/img/soulmate/icons/message.svg?react"
import MoonIcon from "@/assets/img/soulmate/icons/moon.svg?react"
import PaletteIcon from "@/assets/img/soulmate/icons/palette.svg?react"
import PartyIcon from "@/assets/img/soulmate/icons/party.svg?react"
import PlaneIcon from "@/assets/img/soulmate/icons/plane.svg?react"
import { BrainIcon, HeartIcon, SparklesIcon, WaveIcon, type SoulmateIcon } from "./soulmate-icons"

import Emoji1 from "@/assets/img/soulmate/emoji-1.svg"
import Emoji2 from "@/assets/img/soulmate/emoji-2.svg"
import Emoji3 from "@/assets/img/soulmate/emoji-3.svg"
import Emoji4 from "@/assets/img/soulmate/emoji-4.svg"
import Emoji5 from "@/assets/img/soulmate/emoji-5.svg"
import { cn } from "@/lib/utils"
import type { StepComponentProps } from "./step-contract"

/** Icons usable on Soulmate choice options (keyed by `option.icon`). All the
 * quiz's list icons are Figma-exact SVGs (src/assets/img/soulmate/icons, via
 * svgr `?react`, currentColor); only mars/venus/ellipsis (Step1/2) stay lucide. */
const ICONS: Record<string, LucideIcon | SoulmateIcon> = {
  mars: Mars,
  venus: Venus,
  ellipsis: Ellipsis,
  check: Check,
  x: X,
  waves: WaveIcon,
  sparkles: SparklesIcon,
  heart: HeartIcon,
  brain: BrainIcon,
  message: MessageIcon,
  gift: GiftIcon,
  hand: HandIcon,
  gestures: GesturesIcon,
  activities: ActivitiesIcon,
  plane: PlaneIcon,
  moon: MoonIcon,
  party: PartyIcon,
  palette: PaletteIcon,
  home: HomeIcon,
  infinity: InfinityIcon,
  // Astro-Map energy source (Figma-exact: people-cluster / lone figure / scales).
  extrovert: ExtrovertIcon,
  introvert: IntrovertIcon,
  ambivert: AmbivertIcon,
  // Astro-Map elements (Figma-exact alchemical triangles).
  fire: FireIcon,
  water: WaterIcon,
  earth: EarthIcon,
  air: AirIcon,
}

/** Likert emoji faces, strongly-disagree → strongly-agree (Step14). */
const EMOJIS = [Emoji1, Emoji2, Emoji3, Emoji4, Emoji5]

/**
 * Soulmate Sketch single-choice step. Matches the Figma rows 1:1 (no trailing
 * chevron, optional 32px leading icon, `main-purple` selected fill) and adds the
 * design's two extra presentations the shared step can't express — a row of big
 * planet circles (`icons`) and a 5-point emoji Likert scale (`likert`). Wired via
 * the quiz's `stepOverrides["single-choice"]`, so the shared component and the
 * other quizzes are untouched. Auto-advances on selection.
 */
export function SoulmateChoiceStep({ step, value, onChange }: StepComponentProps) {
  const selected = typeof value === "string" ? value : undefined
  const options = step.options ?? []

  // Big circular icon options in a row (Step1 — gender).
  if (step.layout === "icons") {
    return (
      <div className="flex items-start justify-center gap-3">
        {options.map((option) => {
          const Icon = option.icon ? ICONS[option.icon] : undefined
          const isSelected = selected === option.value
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className="flex flex-1 flex-col items-center gap-3"
            >
              <span
                className={cn(
                  "flex size-[min(27vw,6.5rem)] items-center justify-center rounded-full backdrop-blur-md transition-colors",
                  isSelected
                    ? "bg-main-purple text-white"
                    : "bg-purple-light/10 text-purple-light",
                )}
              >
                {Icon ? (
                  <Icon className="size-[min(14vw,3.5rem)]" strokeWidth={1.5} />
                ) : (
                  option.label.charAt(0)
                )}
              </span>
              <span className="text-base font-semibold text-purple-light">{option.label}</span>
            </button>
          )
        })}
      </div>
    )
  }

  // 5-point emoji Likert scale (Step14). A dashed track (SVG stroke — the design's
  // 1px line in purple-light/20, 8/5 round dashes) sits *under* the faces and just
  // above the labels, where it'd be hidden if drawn behind the faces. Figma 1:1.
  if (step.layout === "likert") {
    const selectedIndex = options.findIndex((o) => o.value === selected)
    return (
      <div className="flex flex-col pt-2">
        <div className="flex items-end justify-between">
          {options.map((option, i) => {
            const isSelected = selected === option.value
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange(option.value)}
                className={cn(
                  // Faces stay full-brightness by default (matches the design). Only
                  // once a face is picked do the others dim, so the choice stands out.
                  "transition-all duration-200 hover:opacity-100",
                  isSelected && "scale-110",
                  selectedIndex >= 0 && !isSelected ? "opacity-60" : "opacity-100",
                )}
              >
                <img
                  src={EMOJIS[i] ?? Emoji3}
                  alt={option.label}
                  className="size-[min(15vw,4rem)]"
                />
              </button>
            )
          })}
        </div>
        <div className="mt-4">
          <svg width="100%" height="2" className="block overflow-visible">
            <line
              x1="0"
              y1="1"
              x2="100%"
              y2="1"
              stroke="rgba(223, 203, 245, 0.20)"
              strokeWidth="1"
              strokeDasharray="8 5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div className="mt-[2.5px] flex justify-between text-sm text-purple-light/70">
          <span>{options[0]?.label}</span>
          <span>{options[options.length - 1]?.label}</span>
        </div>
        {selectedIndex >= 0 && <span className="sr-only">{options[selectedIndex]?.label}</span>}
      </div>
    )
  }

  // Vertical list rows, optionally with a 32px leading icon (Steps 2-4, 7-13, 15)
  // and an optional muted second line (Astro-Map energy / element steps).
  return (
    <div className="flex flex-col gap-2 [@media(max-height:740px)]:gap-1.5">
      {options.map((option) => {
        const Icon = option.icon ? ICONS[option.icon] : undefined
        const emoji = option.icon && !Icon ? option.icon : undefined
        const isSelected = selected === option.value
        const hasDescription = Boolean(option.description)
        const hasLead = Boolean(Icon || emoji)
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              "flex w-full items-center gap-3 rounded-xl border-2 text-left backdrop-blur-[15px] transition-colors",
              hasDescription
                ? "py-3 [@media(max-height:740px)]:py-2.5"
                : "h-14 text-base font-semibold [@media(max-height:740px)]:h-12",
              hasLead ? "pr-2 pl-4" : "px-4",
              isSelected
                ? "border-main-purple bg-main-purple/10 text-purple-light"
                : "border-transparent bg-purple-light/10 text-purple-light hover:bg-purple-light/15",
            )}
          >
            {Icon && (
              <Icon
                className={cn("size-8 shrink-0", option.iconColor ?? "text-main-purple")}
                strokeWidth={1.5}
              />
            )}
            {emoji && (
              <span className="flex size-8 shrink-0 items-center justify-center text-[32px] leading-none">
                {emoji}
              </span>
            )}
            {hasDescription ? (
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-base font-semibold leading-tight">{option.label}</span>
                <span className="mt-0.5 text-sm leading-tight text-purple-light/60">
                  {option.description}
                </span>
              </span>
            ) : (
              <span className="flex-1">{option.label}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}
